import { Flex } from 'antd';
import { DateTime } from 'luxon';
import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useMutation } from 'react-relay';
import { v4 as uuidv4 } from 'uuid';

import { COLOR_ITEM, DEFAULT_ITEM_NAME, FORMAT_DATE_TIME, TIMEOUT_BEFORE_AUTO_SAVING } from '../const';
import { calcStatusByPercentageCompletion } from '../functions';
import { changeItemsForMoving } from '../gantChangeData';
import { ScheduleItemTypeInput } from '../graphql/__generated__/CreateScheduleItemMutation.graphql';
import CreateScheduleItemMutation from '../graphql/mutations/CreateScheduleItemMutation';
import useCommitScheduleItem from '../hooks/useCommitScheduleItem';
import { ComponentsDispatch, RootState } from '../store';
import { setterGantItems } from '../store/slices/gantItemsSlice';
import { setterGant } from '../store/slices/gantSlice';
import { addSpinnerItem, removeSpinnerItem } from '../store/slices/spinnerSlice';
import { ItemType, MappingItemIdIndexType, ScheduleItemType } from '../types';

const GantLeftHeaderPanel = () => {
    const dispatch = useDispatch<ComponentsDispatch>();
    const [commitCreate, isInFlightCreate] = useMutation(CreateScheduleItemMutation);
    const { getCommitScheduleItem } = useCommitScheduleItem();

    const currentOffsetDate = useSelector<RootState, string>((state) => state.gant.currentOffsetDate);
    const connectionItemsId = useSelector<RootState, string>((state) => state.gant.connectionItemsId);
    const newExternalItem = useSelector<RootState, ScheduleItemType | null>((state) => state.gant.newExternalItem);
    const appendExternalItem = useSelector<RootState, ScheduleItemType | null>(
        (state) => state.gant.appendExternalItem,
    );
    const itemsList = useSelector<RootState, ItemType[]>((state) => state.gantItems.itemsList);
    const mappingItemIdIndex = useSelector<RootState, MappingItemIdIndexType>((state) => state.gant.mappingItemIdIndex);

    const [lastAddItemId, setLastAddItemId] = useState<string>();

    useEffect(() => {
        if (!lastAddItemId) {
            return;
        }

        setLastAddItemId(undefined);

        setTimeout(() => {
            const newItems = changeItemsForMoving(
                mappingItemIdIndex,
                0,
                'both',
                itemsList[mappingItemIdIndex[lastAddItemId]],
                itemsList,
                [],
            );

            dispatch(
                setterGantItems({
                    isItemEdit: true,
                    currentGantItem: itemsList[mappingItemIdIndex[lastAddItemId]],
                    itemsList: newItems,
                    externalTableScheduleItemPrefixFetchKey: uuidv4(),
                    // countVisibleRow: newItems.filter((item) => !item.hideForCollapse).length,
                }),
            );
        });
    }, [itemsList]);

    const prepareNewItemData = (newItem: ScheduleItemType) => {
        const item = { ...newItem };

        item.id = uuidv4();
        item.name = item.name || `${DEFAULT_ITEM_NAME}${DateTime.now().millisecond}`;
        item.phase_id = +item.phase_id;

        if (!item.parent_id) {
            const parentIdFromPhase = itemsList.find((subItem) => {
                return (
                    subItem.roleItem === 'phase' &&
                    subItem.phaseId === +item.phase_id &&
                    subItem.projectId === item.project_id!
                );
            })?.id;

            const parentIdFromProject = itemsList.find(
                (subItem) => subItem.roleItem === 'project' && subItem.projectId === item.project_id!,
            )?.id!;

            item.parent_id = parentIdFromPhase ? parentIdFromPhase : parentIdFromProject;
        }

        return item;
    };

    const addItemToDataBase = (newItem: ScheduleItemType, append?: boolean) => {
        const preparedItem = prepareNewItemData(newItem);

        const parentItem = itemsList[mappingItemIdIndex[preparedItem.parent_id!]];

        console.log('addItemToDataBase', { preparedItem, parentItem });
        const newItems = [...itemsList];
        newItems[mappingItemIdIndex[preparedItem.parent_id!]] = {
            ...parentItem,
            children: append
                ? [...(parentItem.children || []), preparedItem.id]
                : [preparedItem.id, ...(parentItem.children || [])],
        };

        const getExpandItemIdChain = (item: ItemType, chain: string[] = []): string => {
            chain.unshift(item.id);
            if (item?.parentId && !chain.includes(item.parentId)) {
                return getExpandItemIdChain(itemsList[mappingItemIdIndex[item.parentId]], chain);
            }
            return chain.join(',');
        };

        dispatch(
            setterGantItems({
                itemsList: [...newItems],
            }),
        );

        // update list items for parent
        dispatch(
            setterGant({
                expandItemIds: getExpandItemIdChain(parentItem),
                // countVisibleRow: newItems.filter((item) => !item.hideForCollapse).length,
            }),
        );

        // should wait for start auto saving
        setTimeout(() => {
            commitCreate(
                getCommitScheduleItem(
                    preparedItem as ScheduleItemTypeInput,
                    connectionItemsId,
                    undefined,
                    (id: string) => {
                        setLastAddItemId(id);
                    },
                ),
            );
        }, TIMEOUT_BEFORE_AUTO_SAVING + 1000);
    };

    const appendItemToDataBase = (appendItem: ScheduleItemType) => addItemToDataBase(appendItem, true);

    useEffect(() => {
        if (newExternalItem) {
            dispatch(addSpinnerItem('addScheduleItem'));
            setTimeout(() => {
                addItemToDataBase(newExternalItem);
            }, 1000);
        }
    }, [newExternalItem]);

    useEffect(() => {
        if (appendExternalItem) {
            dispatch(addSpinnerItem('addScheduleItem'));
            setTimeout(() => {
                appendItemToDataBase(appendExternalItem);
            }, 1000);
        }
    }, [appendExternalItem]);

    useEffect(() => {
        if (isInFlightCreate) {
            dispatch(addSpinnerItem('addScheduleItem'));
        } else {
            dispatch(removeSpinnerItem('addScheduleItem'));
        }
    }, [isInFlightCreate]);

    const _onCLickAdd = () => {
        dispatch(addSpinnerItem('addScheduleItem'));
        (async () => {
            const newCurrentDate = DateTime.fromISO(currentOffsetDate) || DateTime.now();
            const data = {
                project_id: '9d77e706-8fe5-4734-84e9-29b941fd4893',
                color: COLOR_ITEM,
                start_datetime: newCurrentDate.toFormat(FORMAT_DATE_TIME),
                end_datetime: newCurrentDate.plus({ days: 3 }).toFormat(FORMAT_DATE_TIME),
                completion_percentage: 0,
                status: calcStatusByPercentageCompletion(0),
                // onlyBusinessDay: false,
            } as ScheduleItemType;

            addItemToDataBase(data);
        })();
    };

    return (
        <Flex justify={'center'} align={'center'} style={{ height: '100%' }}>
            {/*<Button type={'default'} onClick={onCLickAdd}>*/}
            {/*    Add Item*/}
            {/*</Button>*/}
        </Flex>
    );
};

export default React.memo(GantLeftHeaderPanel);
